import React, { useState } from 'react';
import { Avatar, Button, message, Spin } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TeamRequest } from 'models/CommonType';
import { acceptTeamRequest, rejectTeamRequest } from 'services/TeamService';
import './TeamRequestsList.css';

interface TeamRequestsListProps {
  requests: TeamRequest[];
  authKey: string;
  onRequestUpdate: () => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
}

const TeamRequestsList: React.FC<TeamRequestsListProps> = ({
  requests,
  authKey,
  onRequestUpdate,
  isLoading = false,
  hasNextPage = false,
  onLoadMore
}) => {
  const { t } = useTranslation();
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  const handleAcceptRequest = async (request: TeamRequest) => {
    if (processingRequest) return;
    
    setProcessingRequest(request.id);
    try {
      await acceptTeamRequest(request.id, authKey);
      message.success(
        `${request.user.first_name} ${request.user.last_name} has been accepted to the team`
      );
      onRequestUpdate();
    } catch (error: any) {
      message.error(error.message || t('#Teams.accept_request_error'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (request: TeamRequest) => {
    if (processingRequest) return;
    
    setProcessingRequest(request.id);
    try {
      await rejectTeamRequest(request.id, authKey);
      message.success(
        `${request.user.first_name} ${request.user.last_name} request has been rejected`
      );
      onRequestUpdate();
    } catch (error: any) {
      message.error(error.message || t('#Teams.reject_request_error'));
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleUserClick = (userId: string) => {
    const profileUrl = `${window.location.origin}/profile/${userId}`;
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  if (requests.length === 0 && !isLoading) {
    return (
      <div className="team-requests-empty">
        <p className="empty-message">No pending join requests</p>
      </div>
    );
  }

  return (
    <div className="team-requests-list">
      <div className="requests-header">
        <h3 className="requests-title">Join Requests ({requests.length})</h3>
      </div>
      
      <div className="requests-container">
        {requests.map((request) => (
          <div key={request.id} className="request-item">
            <div 
              className="request-user-info"
              onClick={() => request.user.id && handleUserClick(request.user.id)}
            >
              <Avatar
                size={48}
                src={request.user.profile?.image_small_url}
                className="request-user-avatar clickable-avatar"
              >
                {request.user.first_name?.[0]}{request.user.last_name?.[0]}
              </Avatar>
              <div className="request-user-details">
                <div className="request-user-name clickable-name">
                  {request.user.first_name} {request.user.last_name}
                </div>
                <div className="request-user-bio">
                  {request.user.profile?.bio || 'No bio available'}
                </div>
                <div className="request-date">
                  Requested {new Date(request.created).toLocaleDateString()}
                </div>
              </div>
            </div>
            
            <div className="request-actions">
              {request.rejected ? (
                <div className="rejected-status">
                  <span className="rejected-text">Rejected</span>
                  <div className="rejected-date">
                    {new Date(request.rejected).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="action-buttons">
                  <Button
                    type="text"
                    icon={<CheckOutlined />}
                    onClick={() => handleAcceptRequest(request)}
                    loading={processingRequest === request.id}
                    disabled={!!processingRequest}
                    className="accept-icon-button"
                    size="small"
                    title="Accept Request"
                  />
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => handleRejectRequest(request)}
                    loading={processingRequest === request.id}
                    disabled={!!processingRequest}
                    className="reject-icon-button"
                    size="small"
                    title="Reject Request"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="requests-loading">
            <Spin size="small" />
            <span>Loading requests...</span>
          </div>
        )}
        
        {hasNextPage && !isLoading && (
          <div className="load-more-section">
            <Button
              type="link"
              onClick={onLoadMore}
              className="load-more-button"
            >
              Load More Requests
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamRequestsList;